import * as React from 'react';
import { format } from 'date-fns';
import { t } from '~/i18n';
import { AlertEventGlyph } from '~components/AlertLogs/AlertEventIcon';
import styles from './LocationTimeline.module.css';
import { TimelineWindow } from './useTimelineWindow';
import {
  gapAt,
  groupRuns,
  INDOOR_ALT_COLOR,
  INDOOR_COLOR,
  OUTDOOR_COLOR,
  pointAt,
  pointColor,
  ticks,
  TimelineBand,
  TimelineEvent,
  TimelineGap,
  TimelinePoint,
  TimelineSpan,
} from './timeline.utils';

const DOT_SPACING = 12;
const DOT_SIZE = 10;
/** Below this a press reads as a click that pins a moment, above it as a drag that selects a window. */
const DRAG_THRESHOLD = 5;
const EVENT_CLUSTER_DISTANCE = 18;
const EVENT_LANE_HEIGHT = 24;
const TOOLTIP_WIDTH = 280;
const GAP_LABEL_MIN_WIDTH = 110;
const RUN_LABEL_MIN_WIDTH = 80;

type TooltipRow = { time?: string; title: string; detail?: string; color: string };

type LocationTimelineTrackProps = {
  timeline: TimelineWindow;
  points: TimelinePoint[];
  gaps: TimelineGap[];
  events: TimelineEvent[];
  bands: TimelineBand[];
  /** Stretches whose positions the site does not show; drawn and described apart from gaps without data. */
  hidden?: TimelineSpan[];
  onPreview: (time: number | null) => void;
};

export function formatGapDuration(ms: number) {
  const minutes = Math.max(1, Math.round(ms / 60000));
  if (minutes < 60) return `${minutes} min`;
  const rest = minutes % 60;
  return rest ? `${Math.floor(minutes / 60)} h ${rest} min` : `${minutes / 60} h`;
}

function gapLabel(gap: TimelineGap) {
  return gap.ongoing
    ? t('locationTimeline.noNewPositionSince', { time: format(gap.start, 'HH:mm') })
    : t('locationTimeline.noDataFor', { duration: formatGapDuration(gap.end - gap.start) });
}

export function placeLabel(point: TimelinePoint) {
  return (
    point.location.description ||
    t(point.isOutdoor ? 'locationTimeline.outdoorGps' : 'profile-configuration.tracking-mode.indoor')
  );
}

function useWidth(ref: React.RefObject<HTMLDivElement | null>) {
  const [width, setWidth] = React.useState(0);
  React.useEffect(() => {
    if (!ref.current) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

const LocationTimelineTrack: React.FC<LocationTimelineTrackProps> = ({
  timeline,
  points,
  gaps,
  events,
  bands,
  hidden = [],
  onPreview,
}) => {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const width = useWidth(trackRef);
  const [hover, setHover] = React.useState<{ x: number; y: number } | null>(null);
  const [dragStart, setDragStart] = React.useState<number | null>(null);

  const { start, end, span } = timeline;
  const xOf = (time: number) => ((time - start) / span) * width;
  const timeOf = (x: number) => start + (x / width) * span;

  const visiblePoints = React.useMemo(
    () => points.filter((point) => point.time >= start && point.time <= end),
    [points, start, end]
  );
  const visibleGaps = gaps.filter((gap) => gap.end > start && gap.start < end);

  const runs = React.useMemo(
    () => groupRuns(visiblePoints, gaps, t('profile-configuration.tracking-mode.outdoor')),
    [visiblePoints, gaps]
  );

  const clusters = React.useMemo(() => {
    const result: { x: number; items: TimelineEvent[] }[] = [];
    events
      .filter((event) => event.time >= start && event.time <= end)
      .sort((a, b) => a.time - b.time)
      .forEach((event) => {
        const x = ((event.time - start) / span) * width;
        const last = result[result.length - 1];
        if (last && x - last.x < EVENT_CLUSTER_DISTANCE) last.items.push(event);
        else result.push({ x, items: [event] });
      });
    return result;
  }, [events, start, end, span, width]);

  const updateHover = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.min(width, Math.max(0, e.clientX - rect.left));
    setHover({ x, y: e.clientY - rect.top });
    onPreview(timeOf(x));
  };

  const clearHover = () => {
    setHover(null);
    setDragStart(null);
    onPreview(null);
  };

  const release = () => {
    if (dragStart === null || !hover) return;
    if (Math.abs(hover.x - dragStart) > DRAG_THRESHOLD) {
      const [from, to] = [dragStart, hover.x].sort((a, b) => a - b);
      timeline.select(timeOf(from), timeOf(to));
    } else {
      timeline.pin(timeOf(hover.x));
    }
    setDragStart(null);
  };

  const isDragging = dragStart !== null && !!hover && Math.abs(hover.x - dragStart) > DRAG_THRESHOLD;

  const hoverTime = hover ? timeOf(hover.x) : null;
  const hoverHidden =
    hoverTime !== null ? hidden.find((span) => span.start <= hoverTime && hoverTime <= span.end) : undefined;
  const hoverPoint = hoverTime !== null && !hoverHidden ? pointAt(points, hoverTime) : undefined;
  const hoverGap = hoverTime !== null ? gapAt(gaps, hoverTime) : undefined;

  let tooltipRows: TooltipRow[] = [];
  if (hover && hoverTime !== null) {
    if (hover.y < EVENT_LANE_HEIGHT) {
      const cluster = clusters.find((c) => Math.abs(c.x - hover.x) <= EVENT_CLUSTER_DISTANCE / 2 + 2);
      if (cluster) tooltipRows = cluster.items.map((event) => ({ title: event.text, color: event.color }));
    }
    if (!tooltipRows.length) {
      if (hoverHidden) {
        tooltipRows = [
          { time: format(hoverTime, 'HH:mm'), title: t('locationTimeline.hiddenOutsideAlarms'), color: '#b6b6c7' },
        ];
      } else if (!hoverPoint) {
        tooltipRows = [{ time: format(hoverTime, 'HH:mm'), title: t('locationTimeline.noPosition'), color: '#b6b6c7' }];
      } else if (hoverGap) {
        tooltipRows = [
          {
            time: hoverGap.ongoing
              ? undefined
              : `${format(hoverGap.start, 'HH:mm')} – ${format(hoverGap.end, 'HH:mm')}`,
            title: gapLabel(hoverGap),
            detail: t('locationTimeline.lastPosition', { place: placeLabel(hoverPoint) }),
            color: '#b6b6c7',
          },
        ];
      } else {
        // The API reports accuracy in centimetres; the label is in metres, like the map's circle.
        const accuracy =
          hoverPoint.location.discriminator === 'OUTDOOR' && hoverPoint.location.accuracy != null
            ? hoverPoint.location.accuracy / 1e2
            : undefined;
        tooltipRows = [
          {
            time: format(hoverPoint.time, 'HH:mm:ss'),
            title: placeLabel(hoverPoint),
            detail: hoverPoint.isOutdoor
              ? accuracy != null
                ? t('locationTimeline.gpsAccuracy', { accuracy: Math.round(accuracy) })
                : t('profile-configuration.tracking-mode.outdoor')
              : t('profile-configuration.tracking-mode.indoor'),
            color: pointColor(hoverPoint),
          },
        ];
      }
    }
  }

  const loose: TimelinePoint[] = [];
  const bars: { left: number; width: number; color: string; label: string }[] = [];
  let indoorRuns = 0;
  runs.forEach((run) => {
    const left = xOf(run.start);
    const runWidth = xOf(run.end) - left;
    if (run.points.length === 1 || runWidth / run.points.length >= DOT_SPACING) {
      loose.push(...run.points);
      return;
    }
    const color = run.isOutdoor ? OUTDOOR_COLOR : indoorRuns++ % 2 ? INDOOR_ALT_COLOR : INDOOR_COLOR;
    bars.push({ left, width: Math.max(runWidth - 2, 8), color, label: run.label });
  });

  // Dots closer than their own size would overlap, so they merge into one capsule.
  const dots: { left: number; width: number; color: string }[] = [];
  loose
    .sort((a, b) => a.time - b.time)
    .forEach((point) => {
      const x = xOf(point.time) - DOT_SIZE / 2;
      const color = pointColor(point);
      const last = dots[dots.length - 1];
      if (last && last.color === color && x - (last.left + last.width) < 0) {
        last.width = x + DOT_SIZE - last.left;
      } else {
        dots.push({ left: x, width: DOT_SIZE, color });
      }
    });

  const hoverPointX = hoverPoint && !hoverGap ? xOf(hoverPoint.time) : null;
  const pinnedX = !hover && timeline.pinned !== null && timeline.pinned >= start ? xOf(timeline.pinned) : null;

  return (
    <div className={styles.TrackWrapper}>
      <div
        ref={trackRef}
        className={styles.Track}
        onMouseMove={updateHover}
        onMouseLeave={clearHover}
        onMouseDown={(e) => {
          e.preventDefault();
          if (hover) setDragStart(hover.x);
        }}
        onMouseUp={release}
      >
        <div className={styles.Lane} />
        {ticks(start, end).map((time) => (
          <div key={`grid-${time}`} className={styles.GridLine} style={{ left: xOf(time) }} />
        ))}
        {bands
          .filter((band) => band.end > start && band.start < end)
          .map((band) => {
            const left = Math.max(0, xOf(band.start));
            return (
              <div
                key={`band-${band.start}`}
                className={styles.Band}
                style={{
                  left,
                  width: Math.min(width, xOf(band.end)) - left,
                  borderTopColor: band.color,
                  backgroundColor: `${band.color}1f`,
                }}
              />
            );
          })}
        {hidden.map((span) => {
          const left = Math.max(0, xOf(span.start));
          const hiddenWidth = Math.min(width, xOf(span.end)) - left;
          return (
            <div key={`hidden-${span.start}`} className={styles.Hidden} style={{ left, width: hiddenWidth }}>
              {hiddenWidth > GAP_LABEL_MIN_WIDTH && <span>{t('locationTimeline.hiddenOutsideAlarms')}</span>}
            </div>
          );
        })}
        {visibleGaps.map((gap) => {
          const left = Math.max(0, xOf(gap.start));
          const gapWidth = Math.min(width, xOf(gap.end)) - left;
          return (
            <div key={`gap-${gap.start}`} className={styles.Gap} style={{ left, width: Math.max(gapWidth, 2) }}>
              {gapWidth > GAP_LABEL_MIN_WIDTH && (
                <span>
                  {gap.ongoing
                    ? gapLabel(gap)
                    : `${t('locationTimeline.noData')} · ${formatGapDuration(gap.end - gap.start)}`}
                </span>
              )}
            </div>
          );
        })}
        {bars.map((bar) => (
          <div
            key={`run-${bar.left}`}
            className={styles.Run}
            style={{ left: bar.left, width: bar.width, backgroundColor: bar.color }}
          >
            {bar.width > RUN_LABEL_MIN_WIDTH && bar.label}
          </div>
        ))}
        {dots.map((dot) => (
          <div
            key={`dot-${dot.left}`}
            className={styles.Dot}
            style={{ left: dot.left, width: dot.width, backgroundColor: dot.color }}
          />
        ))}
        {clusters.map((cluster) => {
          const latest = cluster.items[cluster.items.length - 1];
          return (
            <div
              key={`event-${cluster.items[0].time}`}
              className={styles.EventMarker}
              style={{ left: cluster.x - 9, backgroundColor: latest.color }}
            >
              {cluster.items.length > 1 ? cluster.items.length : <AlertEventGlyph kind={latest.kind} />}
            </div>
          );
        })}
        {hoverPointX !== null && hoverPointX >= 0 && (
          <div className={styles.Ring} style={{ left: hoverPointX - 8, borderColor: pointColor(hoverPoint!) }} />
        )}
        {pinnedX !== null && pinnedX <= width && <div className={styles.PinnedCursor} style={{ left: pinnedX - 1 }} />}
        {hover && <div className={styles.HoverCursor} style={{ left: hover.x }} />}
        {timeline.live && <div className={styles.LiveEdge} />}
        {ticks(start, end).map((time) => {
          const x = xOf(time);
          if (x < 14 || x > width - 14) return null;
          return (
            <div key={`tick-${time}`} className={styles.Tick} style={{ left: x }}>
              {format(time, 'HH:mm')}
            </div>
          );
        })}
        {isDragging && (
          <div
            className={styles.Selection}
            style={{ left: Math.min(dragStart!, hover!.x), width: Math.abs(hover!.x - dragStart!) }}
          >
            <span>
              {format(timeOf(Math.min(dragStart!, hover!.x)), 'HH:mm')} –{' '}
              {format(timeOf(Math.max(dragStart!, hover!.x)), 'HH:mm')}
            </span>
          </div>
        )}
        {hover && !isDragging && tooltipRows.length > 0 && (
          <div
            className={styles.Tooltip}
            style={{
              left: Math.min(Math.max(0, hover.x - TOOLTIP_WIDTH / 2), width - TOOLTIP_WIDTH),
              width: TOOLTIP_WIDTH,
            }}
          >
            {tooltipRows.map((row, i) => (
              <div key={i} className={styles.TooltipRow}>
                <span className={styles.TooltipDot} style={{ backgroundColor: row.color }} />
                <div>
                  {row.time && <div className={styles.TooltipTime}>{row.time}</div>}
                  <div className={styles.TooltipTitle}>{row.title}</div>
                  {row.detail && <div className={styles.TooltipDetail}>{row.detail}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className={styles.Legend}>
        <span>
          <i style={{ backgroundColor: INDOOR_COLOR }} />
          {t('profile-configuration.tracking-mode.indoor')}
        </span>
        <span>
          <i style={{ backgroundColor: OUTDOOR_COLOR }} />
          {t('locationTimeline.outdoorGps')}
        </span>
        <span>
          <i className={styles.LegendGap} />
          {t('locationTimeline.noData')}
        </span>
        {hidden.length > 0 && (
          <span>
            <i className={styles.LegendHidden} />
            {t('locationTimeline.hidden')}
          </span>
        )}
        {bands.length > 0 && (
          <span>
            <i className={styles.LegendBand} />
            {t('locationTimeline.alarm')}
          </span>
        )}
      </div>
    </div>
  );
};

export default LocationTimelineTrack;
